/**
 * services/session/session.service.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The whole of authentication in SK Company Cash: a probe, a sign-out, and a
 * redirect to the platform when there is no session.
 *
 * THIS PRODUCT OWNS NO AUTH UI. Sign-in, sign-up, password recovery, MFA and
 * email verification all live in skplatform-fe. Building a second login form
 * here would mean a second place for a session bug to hide, and a second thing
 * to keep in step with the platform's contract.
 *
 * WEB     the platform's httpOnly cookies ride on every request
 *         (`withCredentials`). Nothing readable is stored, so an XSS on this
 *         origin cannot lift the session.
 * NATIVE  the platform app hands this app a token over the deep link, which
 *         `storage/tokenStore` persists and the request interceptor sends as a
 *         Bearer header.
 *
 * The `/me` result is cached for a few seconds and de-duplicated, because the
 * shell and the navigator both ask for it during the same boot.
 * ──────────────────────────────────────────────────────────────────────────
 */
import axiosInstance from '../../api/axiosInstance';
import { Platform_Logout_Url, Platform_Me_Url } from '../../api/endpoints';
import { redirectToPlatformAuth } from '../../navigation/platformAuthRedirect';
import { sessionPayloadSchema, type SessionPayload } from '../../schemas/session';
import { clearTokens, saveTokens } from '../../storage/tokenStore';

/** Short TTL: long enough to collapse a boot's parallel callers, no longer. */
const ME_TTL_MS = 5_000;

let cache: { payload: SessionPayload; at: number } | null = null;
let inFlight: Promise<SessionPayload> | null = null;

export const invalidateSession = (): void => {
  cache = null;
};

/**
 * Identity probe. Resolves with the session, or throws — a 401 here means "not
 * signed in", which is a legitimate answer and not an error to toast.
 */
export async function fetchSession(): Promise<SessionPayload> {
  if (cache && Date.now() - cache.at < ME_TTL_MS) return cache.payload;

  if (!inFlight) {
    inFlight = axiosInstance
      .get(Platform_Me_Url)
      .then(({ data }) => {
        // The platform wraps its responses; accept both shapes.
        const body = (data as { data?: unknown })?.data ?? data;
        const parsed = sessionPayloadSchema.parse(body);
        cache = { payload: parsed, at: Date.now() };
        return parsed;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** Clears the platform session, then everything held locally. */
export async function signOut(): Promise<void> {
  try {
    await axiosInstance.post(Platform_Logout_Url);
  } finally {
    invalidateSession();
    await clearTokens();
  }
}

/**
 * Send the visitor to skplatform-fe to sign in.
 *
 * The whole handoff — product slug, return_to, and the `sk_auth_state` cookie
 * the platform verifies — lives in navigation/platformAuthRedirect, shared with
 * the other SK products. Building the URL here instead is what left the console
 * sending only `return_to`, which the platform silently ignores.
 */
export function redirectToSignIn(): void {
  redirectToPlatformAuth();
}

/**
 * Accept a token pair handed over by the platform app's deep link
 * (`skcompanycash://auth?access_token=…&refresh_token=…`). Native only —
 * on web the same hand-off is a cookie the browser already holds.
 */
export async function acceptHandoff(accessToken: string, refreshToken?: string): Promise<void> {
  await saveTokens(accessToken, refreshToken ?? null);
  invalidateSession();
}

export type { SessionPayload };
